import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const AlertsPanel = ({ onRefresh }) => {
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [showResolved, setShowResolved] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [successMsg, setSuccessMsg] = useState('');

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch from system_alerts table
      const { data: systemAlerts, error: saErr } = await supabase?.from('system_alerts')?.select('*')?.order('created_at', { ascending: false })?.limit(50);

      if (saErr) throw saErr;

      // Also derive alerts from real events: failed orders, pending payouts > 24h
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)?.toISOString();

      const [failedOrdersRes, oldPayoutsRes] = await Promise.all([
        supabase?.from('orders')?.select('id, order_number, created_at, cancel_reason')?.eq('status', 'cancelled')?.gte('created_at', yesterday)?.limit(5),
        supabase?.from('payout_requests')?.select('id, amount, created_at, driver:user_profiles!payout_requests_driver_id_fkey(full_name)')?.eq('status', 'pending')?.lt('created_at', yesterday)?.limit(5),
      ]);

      const derivedAlerts = [];

      // Derive alerts from cancelled orders
      if (failedOrdersRes?.data?.length > 0) {
        derivedAlerts?.push({
          id: 'derived-cancelled-orders',
          title: `${failedOrdersRes?.data?.length} Cancelled Orders in Last 24h`,
          description: `${failedOrdersRes?.data?.length} orders were cancelled in the last 24 hours. Review for patterns or service issues.`,
          severity: failedOrdersRes?.data?.length > 3 ? 'high' : 'medium',
          category: 'operations',
          status: 'active',
          affected_services: ['Order Management'],
          action_required: failedOrdersRes?.data?.length > 3,
          created_at: new Date()?.toISOString(),
          isDerived: true,
        });
      }

      // Derive alerts from overdue payouts
      if (oldPayoutsRes?.data?.length > 0) {
        const totalAmount = oldPayoutsRes?.data?.reduce((s, p) => s + parseFloat(p?.amount || 0), 0);
        derivedAlerts?.push({
          id: 'derived-overdue-payouts',
          title: `${oldPayoutsRes?.data?.length} Payout Requests Pending Over 24h`,
          description: `KES ${totalAmount?.toLocaleString()} in payout requests have been pending for more than 24 hours and require admin action.`,
          severity: 'high',
          category: 'payment',
          status: 'active',
          affected_services: ['Payment Gateway', 'Wallet System'],
          action_required: true,
          created_at: new Date()?.toISOString(),
          isDerived: true,
        });
      }

      setAlerts([...derivedAlerts, ...(systemAlerts || [])]);
    } catch (err) {
      setError(err?.message || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useEffect(() => {
    if (onRefresh) onRefresh(fetchAlerts);
  }, [onRefresh, fetchAlerts]);

  // Real-time subscription for system_alerts
  useEffect(() => {
    const channel = supabase?.channel('system_alerts_changes')?.on('postgres_changes', { event: '*', schema: 'public', table: 'system_alerts' }, () => {
        fetchAlerts();
      })?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [fetchAlerts]);

  const handleAlertAction = async (alertId, action, isDerived) => {
    if (isDerived) {
      setSuccessMsg(`Action "${action}" noted for derived alert`);
      setTimeout(() => setSuccessMsg(''), 3000);
      return;
    }

    setActionLoading(prev => ({ ...prev, [alertId]: action }));
    try {
      const updates = {};
      const now = new Date()?.toISOString();
      const { data: { user } } = await supabase?.auth?.getUser();

      if (action === 'acknowledge') {
        updates.status = 'acknowledged';
        updates.acknowledged_by = user?.id;
        updates.acknowledged_at = now;
      } else if (action === 'resolve') {
        updates.status = 'resolved';
        updates.resolved_by = user?.id;
        updates.resolved_at = now;
      } else if (action === 'escalate') {
        updates.status = 'investigating';
        updates.escalated_by = user?.id;
        updates.escalated_at = now;
      }

      const { error: updateErr } = await supabase?.from('system_alerts')?.update(updates)?.eq('id', alertId);

      if (updateErr) throw updateErr;

      setSuccessMsg(`Alert ${action}d successfully`);
      setTimeout(() => setSuccessMsg(''), 3000);
      fetchAlerts();
    } catch (err) {
      setError(err?.message || `Failed to ${action} alert`);
      setTimeout(() => setError(null), 4000);
    } finally {
      setActionLoading(prev => ({ ...prev, [alertId]: null }));
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'secondary';
      case 'low': return 'primary';
      case 'info': return 'success';
      default: return 'text-muted';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return 'AlertTriangle';
      case 'high': return 'AlertCircle';
      case 'medium': return 'Info';
      case 'low': return 'Bell';
      case 'info': return 'CheckCircle';
      default: return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'error';
      case 'investigating': return 'warning';
      case 'monitoring': return 'secondary';
      case 'acknowledged': return 'primary';
      case 'resolved': return 'success';
      default: return 'text-muted';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'system': return 'Server';
      case 'payment': return 'CreditCard';
      case 'operations': return 'Users';
      case 'security': return 'Shield';
      case 'customer_service': return 'MessageSquare';
      case 'technical': return 'Settings';
      default: return 'Bell';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return new Date(timestamp)?.toLocaleDateString();
  };

  const filteredAlerts = alerts?.filter(alert => {
    if (!showResolved && alert?.status === 'resolved') return false;
    if (selectedSeverity !== 'all' && alert?.severity !== selectedSeverity) return false;
    return true;
  });

  const severityOptions = [
    { value: 'all', label: 'All Severities', count: alerts?.length },
    { value: 'critical', label: 'Critical', count: alerts?.filter(a => a?.severity === 'critical')?.length },
    { value: 'high', label: 'High', count: alerts?.filter(a => a?.severity === 'high')?.length },
    { value: 'medium', label: 'Medium', count: alerts?.filter(a => a?.severity === 'medium')?.length },
    { value: 'low', label: 'Low', count: alerts?.filter(a => a?.severity === 'low')?.length },
  ];

  const activeAlertsCount = alerts?.filter(a => a?.status === 'active')?.length;
  const criticalAlertsCount = alerts?.filter(a => a?.severity === 'critical' && a?.status === 'active')?.length;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-semibold text-text-primary">System Alerts</h3>
          {criticalAlertsCount > 0 && (
            <div className="flex items-center space-x-1 px-2 py-1 bg-error-100 text-error rounded-full">
              <Icon name="AlertTriangle" size={12} />
              <span className="text-xs font-medium">{criticalAlertsCount} Critical</span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowResolved(!showResolved)}
            className={`p-2 rounded-lg transition-colors duration-200 ${showResolved ? 'bg-primary text-white' : 'hover:bg-surface-hover'}`}
            title="Toggle resolved alerts"
          >
            <Icon name="Eye" size={16} />
          </button>
          <button
            onClick={fetchAlerts}
            className="p-2 hover:bg-surface-hover rounded-lg transition-colors duration-200"
            title="Refresh alerts"
          >
            <Icon name="RefreshCw" size={16} />
          </button>
        </div>
      </div>
      {successMsg && (
        <div className="mb-3 p-2 bg-success-50 border border-success-200 rounded-lg text-success text-sm">{successMsg}</div>
      )}
      {error && (
        <div className="mb-3 p-2 bg-error-50 border border-error-200 rounded-lg text-error text-sm">{error}</div>
      )}
      {/* Alert Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="p-3 bg-error-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-error">{criticalAlertsCount}</p>
          <p className="text-xs text-error">Critical</p>
        </div>
        <div className="p-3 bg-warning-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-warning">{alerts?.filter(a => a?.severity === 'high' && a?.status === 'active')?.length}</p>
          <p className="text-xs text-warning">High</p>
        </div>
        <div className="p-3 bg-secondary-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-secondary">{activeAlertsCount}</p>
          <p className="text-xs text-secondary">Active</p>
        </div>
        <div className="p-3 bg-success-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-success">{alerts?.filter(a => a?.status === 'resolved')?.length}</p>
          <p className="text-xs text-success">Resolved</p>
        </div>
      </div>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-primary-50 rounded-lg">
        <select
          value={selectedSeverity}
          onChange={(e) => setSelectedSeverity(e?.target?.value)}
          className="input-field text-sm py-1 px-2 min-w-0"
        >
          {severityOptions?.map((option) => (
            <option key={option?.value} value={option?.value}>
              {option?.label} ({option?.count})
            </option>
          ))}
        </select>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="showResolved"
            checked={showResolved}
            onChange={(e) => setShowResolved(e?.target?.checked)}
            className="rounded"
          />
          <label htmlFor="showResolved" className="text-sm text-text-primary">
            Show resolved alerts
          </label>
        </div>
      </div>
      {/* Alerts List */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3]?.map(i => (
            <div key={i} className="p-4 border border-border rounded-lg animate-pulse">
              <div className="h-4 bg-surface-hover rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-surface-hover rounded w-full"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredAlerts?.map((alert) => (
            <div key={alert?.id} className={`p-4 border rounded-lg transition-all duration-200 hover:shadow-md ${
              alert?.severity === 'critical' ? 'border-error bg-error-50' :
              alert?.severity === 'high' ? 'border-warning bg-warning-50' : 'border-border bg-surface'
            }`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-${getSeverityColor(alert?.severity)}-100`}>
                    <Icon name={getSeverityIcon(alert?.severity)} size={16} color={`var(--color-${getSeverityColor(alert?.severity)})`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1 flex-wrap gap-1">
                      <h4 className="font-medium text-text-primary">{alert?.title}</h4>
                      <span className={`text-xs px-2 py-1 rounded-full bg-${getSeverityColor(alert?.severity)}-100 text-${getSeverityColor(alert?.severity)}`}>
                        {alert?.severity}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full bg-${getStatusColor(alert?.status)}-100 text-${getStatusColor(alert?.status)}`}>
                        {alert?.status}
                      </span>
                      {alert?.isDerived && (
                        <span className="text-xs px-2 py-1 rounded-full bg-primary-100 text-primary">live</span>
                      )}
                    </div>
                    <p className="text-sm text-text-muted mb-2">{alert?.description}</p>
                    <div className="flex items-center space-x-4 text-xs text-text-muted flex-wrap gap-1">
                      <div className="flex items-center space-x-1">
                        <Icon name={getCategoryIcon(alert?.category)} size={12} />
                        <span>{alert?.category?.replace('_', ' ')}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Clock" size={12} />
                        <span>{formatTime(alert?.created_at)}</span>
                      </div>
                      {alert?.affected_services?.length > 0 && (
                        <div className="flex items-center space-x-1">
                          <Icon name="Server" size={12} />
                          <span>{alert?.affected_services?.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {alert?.status !== 'resolved' && (
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="flex flex-wrap gap-2">
                    {alert?.status === 'active' && (
                      <button
                        onClick={() => handleAlertAction(alert?.id, 'acknowledge', alert?.isDerived)}
                        disabled={actionLoading?.[alert?.id] === 'acknowledge'}
                        className="btn-secondary text-xs px-3 py-1 disabled:opacity-50"
                      >
                        {actionLoading?.[alert?.id] === 'acknowledge' ? 'Working...' : 'Acknowledge'}
                      </button>
                    )}
                    <button
                      onClick={() => handleAlertAction(alert?.id, 'resolve', alert?.isDerived)}
                      disabled={actionLoading?.[alert?.id] === 'resolve'}
                      className="btn-secondary text-xs px-3 py-1 disabled:opacity-50"
                    >
                      {actionLoading?.[alert?.id] === 'resolve' ? 'Working...' : 'Resolve'}
                    </button>
                    <button
                      onClick={() => handleAlertAction(alert?.id, 'escalate', alert?.isDerived)}
                      disabled={actionLoading?.[alert?.id] === 'escalate'}
                      className="btn-primary text-xs px-3 py-1 disabled:opacity-50"
                    >
                      {actionLoading?.[alert?.id] === 'escalate' ? 'Working...' : 'Escalate'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {filteredAlerts?.length === 0 && !loading && (
        <div className="text-center py-8">
          <Icon name="CheckCircle" size={48} color="var(--color-success)" className="mx-auto mb-4" />
          <p className="text-text-muted">No alerts match your current filters</p>
        </div>
      )}
      {/* Footer Actions */}
      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
        <p className="text-sm text-text-muted">
          Showing {filteredAlerts?.length} of {alerts?.length} alerts
        </p>
        <button
          onClick={fetchAlerts}
          className="btn-secondary text-sm"
        >
          Refresh
        </button>
      </div>
    </div>
  );
};

export default AlertsPanel;